/**
 * Export branded a PDF del reporte de finanzas. Compañero del export
 * Excel — mismo origen de datos, distinta entrega:
 *
 *   - Portada con banner morado, wordmark y subtítulo cyan
 *   - KPIs grid (ingresos, gastos, margen, IVA neto)
 *   - Cálculo fiscal RESICO PM
 *   - Tabla de transacciones del periodo (paginada automáticamente)
 *   - Agregado por categoría
 *   - Header + footer branded en cada página
 *
 * Bundle pesa ~800KB y se carga lazy (dynamic import) solo en el click.
 */
import type { Transaction } from "@axorial/shared";

type TxWithMeta = Transaction & { companyName: string | null };

interface ExportInput {
  transactions: TxWithMeta[];
  period: string | null;
  accountById: Map<string, string>;
  categoryById: Map<string, string>;
}

const BRAND = {
  purpleDeep: "#2D1B4E",
  purple: "#4A3A8C",
  purpleSoft: "#EAE5F7",
  cyan: "#2DB5C9",
  cyanSoft: "#D8F1F5",
  orange: "#E78A3C",
  stripe: "#F6F4FB",
  border: "#D8D3E3",
  text: "#1F1431",
  textMuted: "#6B6586",
  success: "#159E5C",
  error: "#C93030",
} as const;

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  transfer: "Transferencia",
  card_credit: "Tarjeta crédito",
  card_debit: "Tarjeta débito",
  cash: "Efectivo",
};
const INVOICE_STATUS_LABEL: Record<string, string> = {
  facturado: "Facturado",
  deducible_sin_factura: "Ded. s/factura",
  pendiente: "Pendiente",
  no_aplica: "No aplica",
  olvidado: "Olvidado",
};
const KIND_LABEL: Record<string, string> = {
  ingreso: "Ingreso",
  gasto: "Gasto",
  transferencia: "Transferencia",
};

const fromCents = (cents: number) => Math.round(cents) / 100;

function fmtMxn(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents) / 100;
  return `${sign}$${abs.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function periodLabel(period: string | null): string {
  if (!period) return "Sin periodo";
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return new Date(y, m - 1, 1).toLocaleDateString("es-MX", {
    month: "long",
    year: "numeric",
  });
}

function nowLabel(): string {
  return new Date().toLocaleString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch("/logo/Logo-Axorial-Calado-Blanco.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Captura las gráficas marcadas con `data-pdf-chart="<key>"` del DOM.
 * Si la app está en modo oscuro, fuerza temporalmente el tema claro
 * para que el screenshot quede legible sobre papel/PDF blanco. Restaura
 * el tema original al terminar.
 */
async function captureCharts(keys: string[]): Promise<Record<string, string | null>> {
  const out: Record<string, string | null> = {};
  if (keys.length === 0) return out;

  const html = document.documentElement;
  const originalTheme = html.getAttribute("data-theme");
  const wasDark = originalTheme === "axorial-dark";

  if (wasDark) {
    html.setAttribute("data-theme", "axorial");
    // Recharts re-renderea con los colores nuevos (oklch grids) en el
    // siguiente frame. Esperamos dos frames + un pequeño buffer.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
    await new Promise((r) => setTimeout(r, 250));
  }

  try {
    const { toPng } = await import("html-to-image");
    for (const key of keys) {
      const el = document.querySelector(`[data-pdf-chart="${key}"]`) as HTMLElement | null;
      if (!el) {
        out[key] = null;
        continue;
      }
      try {
        out[key] = await toPng(el, {
          pixelRatio: 2,
          backgroundColor: "#FFFFFF",
          cacheBust: true,
        });
      } catch (e) {
        console.error("capture chart failed", key, e);
        out[key] = null;
      }
    }
  } finally {
    if (wasDark && originalTheme) {
      html.setAttribute("data-theme", originalTheme);
    }
  }

  return out;
}

export async function exportFinancePdf(input: ExportInput): Promise<void> {
  // pdfmake y su VFS tienen forma de export distinta según bundler. Los
  // tomamos como `any` y extraemos lo que toque en runtime.
  const pdfMakeMod = (await import("pdfmake/build/pdfmake")) as unknown as Record<string, unknown>;
  const pdfFontsMod = (await import("pdfmake/build/vfs_fonts")) as unknown as Record<
    string,
    unknown
  >;
  const pdfMake = ((pdfMakeMod.default as Record<string, unknown> | undefined) ?? pdfMakeMod) as {
    createPdf: (doc: import("pdfmake/interfaces").TDocumentDefinitions) => {
      download: (filename: string) => void;
    };
    vfs?: Record<string, string>;
  };
  const fontsDefault = pdfFontsMod.default as Record<string, unknown> | undefined;
  const vfs = (fontsDefault?.vfs ?? pdfFontsMod.vfs ?? fontsDefault ?? pdfFontsMod) as Record<
    string,
    string
  >;
  pdfMake.vfs = vfs;

  const { transactions, period, accountById, categoryById } = input;
  const logo = await loadLogoDataUrl();
  // Capturar gráficas del DOM en paralelo con datos. Si la página no
  // las renderiza (otra tab), los keys faltantes regresan null y el
  // PDF las omite.
  const charts = await captureCharts([
    "tendencia",
    "gastos-categoria",
    "margen-proyecto",
    "salud-financiera",
  ]);

  // ── Totales ──────────────────────────────────────────────────────────
  let ingNeto = 0,
    ingIva = 0,
    gasNeto = 0,
    gasIva = 0,
    ingCobNeto = 0,
    ingCobIva = 0,
    gasPagNeto = 0,
    gasPagIva = 0;
  for (const t of transactions) {
    if (t.kind === "ingreso") {
      ingNeto += t.subtotalCents;
      ingIva += t.ivaCents;
      if (t.paymentStatus === "cobrado") {
        ingCobNeto += t.subtotalCents;
        ingCobIva += t.ivaCents;
      }
    } else if (t.kind === "gasto") {
      gasNeto += t.subtotalCents;
      gasIva += t.ivaCents;
      if (t.paymentStatus === "pagado") {
        gasPagNeto += t.subtotalCents;
        gasPagIva += t.ivaCents;
      }
    }
  }
  const baseIsr = Math.max(0, ingCobNeto - gasPagNeto);
  const isr = Math.round(baseIsr * 0.3);
  const ivaSaldo = ingCobIva - gasPagIva;
  const margenNeto = ingNeto - gasNeto;
  const ivaNeto = ingIva - gasIva;

  // ── Sorted transactions ──────────────────────────────────────────────
  const sortedTx = [...transactions].toSorted((a, b) =>
    (a.paymentDate ?? a.date).localeCompare(b.paymentDate ?? b.date),
  );

  // ── Aggregate by category ────────────────────────────────────────────
  const byCat = new Map<
    string,
    { ingreso: number; ingresoIva: number; gasto: number; gastoIva: number }
  >();
  for (const t of transactions) {
    if (t.kind === "transferencia") continue;
    const name = t.categoryId ? (categoryById.get(t.categoryId) ?? "—") : "Sin categoría";
    let b = byCat.get(name);
    if (!b) {
      b = { ingreso: 0, ingresoIva: 0, gasto: 0, gastoIva: 0 };
      byCat.set(name, b);
    }
    if (t.kind === "ingreso") {
      b.ingreso += t.subtotalCents;
      b.ingresoIva += t.ivaCents;
    } else {
      b.gasto += t.subtotalCents;
      b.gastoIva += t.ivaCents;
    }
  }
  const catRows = [...byCat.entries()].toSorted((a, b) => a[0].localeCompare(b[0]));

  // ── Doc definition ───────────────────────────────────────────────────
  type TxRow = { kind: string; date: string; concepto: string; cuenta: string; total: number };
  const cfdiOk = sortedTx.filter((t) => {
    if (t.kind === "transferencia") return false;
    if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") return false;
    return t.invoiceUuid !== null || t.documentedExternally;
  }).length;
  const cfdiPend = sortedTx.filter((t) => {
    if (t.kind === "transferencia") return false;
    if (t.invoiceStatus === "no_aplica" || t.invoiceStatus === "olvidado") return false;
    return !t.invoiceUuid && !t.documentedExternally;
  }).length;

  const titlePeriod = periodLabel(period);

  // Banner uniforme = pageMargins.top consistente y sin huecos
  // entre banner y contenido en páginas internas.
  const BANNER_H = 70;
  const docDef: import("pdfmake/interfaces").TDocumentDefinitions = {
    pageSize: "LETTER",
    pageMargins: [36, BANNER_H + 18, 36, 56],
    info: {
      title: `Axorial Finanzas — ${titlePeriod}`,
      author: "Axorial Consultoría",
      subject: "Reporte mensual de finanzas RESICO PM",
      creator: "Axorial Panel",
    },
    defaultStyle: {
      font: "Roboto",
      color: BRAND.text,
      fontSize: 9,
    },
    styles: {
      h1: { fontSize: 18, bold: true, color: BRAND.purple, margin: [0, 12, 0, 4] },
      h2: {
        fontSize: 11,
        bold: true,
        color: BRAND.purple,
        margin: [0, 14, 0, 6],
        characterSpacing: 1,
      },
      kpiLabel: { fontSize: 7, color: BRAND.textMuted, bold: true, characterSpacing: 1 },
      kpiValue: { fontSize: 14, bold: true, color: BRAND.text },
      kpiHint: { fontSize: 7, color: BRAND.textMuted },
      tableHeader: { fontSize: 8, bold: true, color: "#FFFFFF" },
      tableCell: { fontSize: 8, color: BRAND.text },
      muted: { color: BRAND.textMuted },
      success: { color: BRAND.success, bold: true },
      error: { color: BRAND.error, bold: true },
    },

    header: (currentPage) => ({
      stack: [
        {
          canvas: [
            { type: "rect", x: 0, y: 0, w: 612, h: BANNER_H, color: BRAND.purpleDeep },
            { type: "rect", x: 0, y: BANNER_H, w: 612, h: 3, color: BRAND.cyan },
          ],
        },
        {
          absolutePosition: { x: 36, y: 14 },
          columns: [
            logo
              ? { image: logo, width: 150, alignment: "left" }
              : {
                  text: "AXORIAL",
                  color: "#FFFFFF",
                  fontSize: 18,
                  bold: true,
                  margin: [0, 12, 0, 0],
                },
            {
              stack: [
                {
                  text: currentPage === 1 ? "Reporte de finanzas" : "Reporte de finanzas",
                  color: "#FFFFFF",
                  fontSize: 12,
                  alignment: "right",
                  margin: [0, 10, 0, 0],
                },
                {
                  text: titlePeriod,
                  color: BRAND.cyanSoft,
                  fontSize: 10,
                  italics: true,
                  alignment: "right",
                  margin: [0, 2, 0, 0],
                },
              ],
            },
          ],
        },
      ],
    }),

    footer: (currentPage, pageCount) => ({
      margin: [36, 12, 36, 0],
      columns: [
        {
          text: "Axorial Panel · RESICO PM",
          fontSize: 7,
          color: BRAND.textMuted,
        },
        {
          text: `Generado ${nowLabel()}`,
          fontSize: 7,
          color: BRAND.textMuted,
          alignment: "center",
        },
        {
          text: `Página ${currentPage} de ${pageCount}`,
          fontSize: 7,
          color: BRAND.textMuted,
          alignment: "right",
        },
      ],
    }),

    content: [
      // ─── Bloque 1: KPIs grid ───
      { text: "Indicadores del periodo", style: "h2" },
      {
        table: {
          widths: ["*", "*", "*", "*"],
          body: [
            [
              kpiCell("Ingresos (neto)", fmtMxn(ingNeto), `IVA ${fmtMxn(ingIva)}`, BRAND.success),
              kpiCell("Gastos (neto)", fmtMxn(gasNeto), `IVA ${fmtMxn(gasIva)}`, BRAND.error),
              kpiCell(
                "Utilidad bruta",
                fmtMxn(margenNeto),
                "Ingresos − gastos",
                margenNeto >= 0 ? BRAND.success : BRAND.error,
              ),
              kpiCell(
                "IVA neto",
                fmtMxn(ivaNeto),
                ivaNeto > 0 ? "A pagar" : ivaNeto < 0 ? "A favor" : "Equilibrado",
                ivaNeto > 0 ? BRAND.orange : BRAND.success,
              ),
            ],
          ],
        },
        layout: kpiLayout(),
      },

      // ─── Bloque opcional: Gráficas (capturadas del DOM) ───
      ...buildChartsSection(charts),

      // ─── Bloque 2: Cálculo fiscal ───
      { text: "Cálculo fiscal — RESICO PM", style: "h2" },
      {
        table: {
          widths: ["*", 110],
          body: [
            tableHeader(["Concepto", "Monto"], 1),
            ...[
              ["Ingresos cobrados (neto)", ingCobNeto],
              ["Gastos pagados (neto)", gasPagNeto],
              ["Base ISR (ingresos − gastos)", baseIsr],
              ["ISR a pagar (30%)", isr],
              ["IVA trasladado (cobrado)", ingCobIva],
              ["IVA acreditable (pagado)", gasPagIva],
              [
                `IVA saldo (${ivaSaldo > 0 ? "a pagar" : ivaSaldo < 0 ? "a favor" : "equilibrado"})`,
                ivaSaldo,
              ],
            ].map(([label, amt], i) => moneyRow(label as string, amt as number, i)),
          ],
        },
        layout: bodyLayout(),
      },

      // ─── Bloque 3: Cumplimiento de facturación ───
      { text: "Cumplimiento de facturación", style: "h2" },
      {
        table: {
          widths: ["*", "*", "*"],
          body: [
            [
              kpiCell(
                "Total movimientos",
                String(transactions.length),
                "Ingreso + gasto",
                BRAND.purple,
              ),
              kpiCell("Con CFDI o declaradas", String(cfdiOk), "", BRAND.success),
              kpiCell(
                "Pendientes de CFDI",
                String(cfdiPend),
                "Requieren acción",
                cfdiPend > 0 ? BRAND.error : BRAND.textMuted,
              ),
            ],
          ],
        },
        layout: kpiLayout(),
      },

      // ─── Bloque 4: Por categoría ───
      { text: "Agregado por categoría", style: "h2" },
      {
        table: {
          headerRows: 1,
          widths: ["*", 70, 70, 70, 70],
          body: [
            tableHeader(["Categoría", "Ingresos", "IVA tras.", "Gastos", "IVA acred."], 1),
            ...catRows.map(([name, v], i) => catRow(name, v, i)),
            catTotalRow(catRows),
          ],
        },
        layout: bodyLayout(),
      },

      // ─── Bloque 5: Transacciones ───
      { text: "Transacciones del periodo", style: "h2", pageBreak: "before" },
      {
        table: {
          headerRows: 1,
          widths: [48, 36, "*", 70, 60, 60],
          body: [
            tableHeader(
              ["Fecha", "Tipo", "Concepto / contraparte", "Cuenta", "Estado", "Total"],
              5,
            ),
            ...sortedTx.map((t, i) => txRow(t, i, accountById)),
          ],
        },
        layout: bodyLayout(),
      },
    ],
  };

  pdfMake
    .createPdf(docDef)
    .download(
      period
        ? `axorial-finanzas-${period}.pdf`
        : `axorial-finanzas-${new Date().toISOString().slice(0, 10)}.pdf`,
    );

  // Reference TxRow so TS no flag de unused.
  void ({} as TxRow);
}

// ─── Helpers ────────────────────────────────────────────────────────────

function kpiCell(
  label: string,
  value: string,
  hint: string,
  accent: string,
): import("pdfmake/interfaces").TableCell {
  return {
    stack: [
      { text: label.toUpperCase(), style: "kpiLabel" },
      { text: value, style: "kpiValue", color: accent, margin: [0, 4, 0, 2] },
      { text: hint, style: "kpiHint" },
    ],
    margin: [10, 10, 10, 10],
    fillColor: BRAND.stripe,
  };
}

function kpiLayout(): import("pdfmake/interfaces").CustomTableLayout {
  return {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
    fillColor: () => null,
  };
}

function bodyLayout(): import("pdfmake/interfaces").CustomTableLayout {
  return {
    hLineWidth: (i, node) => (i === 0 || i === 1 || i === (node.table.body?.length ?? 0) ? 1 : 0.4),
    vLineWidth: () => 0,
    hLineColor: (i) => (i <= 1 ? BRAND.purple : BRAND.border),
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 4,
  };
}

function tableHeader(
  labels: string[],
  lastRightAligned: number,
): import("pdfmake/interfaces").TableCell[] {
  return labels.map((l, i) => ({
    text: l,
    style: "tableHeader",
    fillColor: BRAND.purple,
    alignment: i >= labels.length - lastRightAligned ? "right" : "left",
    margin: [0, 2, 0, 2],
  }));
}

function moneyRow(
  label: string,
  cents: number,
  i: number,
): import("pdfmake/interfaces").TableCell[] {
  const stripe = i % 2 === 1;
  return [
    {
      text: label,
      style: "tableCell",
      fillColor: stripe ? BRAND.stripe : undefined,
    },
    {
      text: fmtMxn(cents),
      style: "tableCell",
      alignment: "right",
      bold: true,
      color: cents < 0 ? BRAND.error : cents > 0 ? BRAND.text : BRAND.textMuted,
      fillColor: stripe ? BRAND.stripe : undefined,
    },
  ];
}

function txRow(
  t: TxWithMeta,
  i: number,
  accountById: Map<string, string>,
): import("pdfmake/interfaces").TableCell[] {
  const stripe = i % 2 === 1;
  const sign = t.kind === "gasto" ? -1 : 1;
  const total = sign * fromCents(t.totalCents);
  const totalLabel =
    total === 0 ? fmtMxn(0) : total > 0 ? `+${fmtMxn(t.totalCents)}` : `-${fmtMxn(t.totalCents)}`;

  const kindColor =
    t.kind === "ingreso" ? BRAND.success : t.kind === "gasto" ? BRAND.error : BRAND.cyan;
  const totalColor = total > 0 ? BRAND.success : total < 0 ? BRAND.error : BRAND.text;
  const fill: string | undefined = stripe ? BRAND.stripe : undefined;
  const fechaDisp = t.paymentDate ?? t.date;
  const concepto =
    t.title?.trim() || t.description?.trim() || t.companyName || t.counterparty || "—";
  const contraparte = t.companyName ?? t.counterparty ?? "";
  const conceptoFull =
    contraparte && contraparte !== concepto ? `${concepto}\n${contraparte}` : concepto;

  return [
    { text: fechaDisp, style: "tableCell", fillColor: fill },
    {
      text: KIND_LABEL[t.kind] ?? t.kind,
      style: "tableCell",
      color: kindColor,
      bold: true,
      fillColor: fill,
    },
    { text: conceptoFull, style: "tableCell", fillColor: fill },
    {
      text: accountById.get(t.accountId) ?? "",
      style: "tableCell",
      fillColor: fill,
      color: BRAND.textMuted,
    },
    {
      text: INVOICE_STATUS_LABEL[t.invoiceStatus] ?? t.invoiceStatus,
      style: "tableCell",
      fillColor: fill,
      color: BRAND.textMuted,
    },
    {
      text: totalLabel,
      style: "tableCell",
      alignment: "right",
      bold: true,
      color: totalColor,
      fillColor: fill,
    },
  ];
}

type Cell = import("pdfmake/interfaces").TableCell;

function catRow(
  name: string,
  v: { ingreso: number; ingresoIva: number; gasto: number; gastoIva: number },
  i: number,
): Cell[] {
  const fill: string | undefined = i % 2 ? BRAND.stripe : undefined;
  return [
    { text: name, style: "tableCell", fillColor: fill },
    {
      text: fmtMxn(v.ingreso),
      style: "tableCell",
      alignment: "right",
      fillColor: fill,
      color: v.ingreso > 0 ? BRAND.success : BRAND.text,
    },
    {
      text: fmtMxn(v.ingresoIva),
      style: "tableCell",
      alignment: "right",
      fillColor: fill,
      color: BRAND.textMuted,
    },
    {
      text: fmtMxn(v.gasto),
      style: "tableCell",
      alignment: "right",
      fillColor: fill,
      color: v.gasto > 0 ? BRAND.error : BRAND.text,
    },
    {
      text: fmtMxn(v.gastoIva),
      style: "tableCell",
      alignment: "right",
      fillColor: fill,
      color: BRAND.textMuted,
    },
  ];
}

function catTotalRow(
  catRows: [string, { ingreso: number; ingresoIva: number; gasto: number; gastoIva: number }][],
): Cell[] {
  const totalIng = catRows.reduce((s, [, v]) => s + v.ingreso, 0);
  const totalIngIva = catRows.reduce((s, [, v]) => s + v.ingresoIva, 0);
  const totalGas = catRows.reduce((s, [, v]) => s + v.gasto, 0);
  const totalGasIva = catRows.reduce((s, [, v]) => s + v.gastoIva, 0);
  return [
    {
      text: "TOTAL",
      style: "tableCell",
      bold: true,
      fillColor: BRAND.purpleSoft,
      color: BRAND.purple,
    },
    {
      text: fmtMxn(totalIng),
      style: "tableCell",
      bold: true,
      alignment: "right",
      fillColor: BRAND.purpleSoft,
      color: BRAND.success,
    },
    {
      text: fmtMxn(totalIngIva),
      style: "tableCell",
      bold: true,
      alignment: "right",
      fillColor: BRAND.purpleSoft,
      color: BRAND.purple,
    },
    {
      text: fmtMxn(totalGas),
      style: "tableCell",
      bold: true,
      alignment: "right",
      fillColor: BRAND.purpleSoft,
      color: BRAND.error,
    },
    {
      text: fmtMxn(totalGasIva),
      style: "tableCell",
      bold: true,
      alignment: "right",
      fillColor: BRAND.purpleSoft,
      color: BRAND.purple,
    },
  ];
}

/**
 * Construye la sección "Gráficas" del PDF. Las gráficas con detalle
 * (timeline, bars de categoría, bars de proyecto) van full-width para
 * que cada label / barra sea legible. El gauge de salud va más chico
 * porque no gana legibilidad creciéndolo.
 *
 * Sin page-breaks forzados — dejamos que pdfmake fluya. Cada chart
 * lleva `pageBreak: "before"` SOLO cuando explícitamente lo decidimos
 * (por ahora ninguno).
 */
function hasAnyChart(charts: Record<string, string | null>): boolean {
  return Object.values(charts).some((v) => v !== null);
}

function buildChartsSection(
  charts: Record<string, string | null>,
): import("pdfmake/interfaces").Content[] {
  if (!hasAnyChart(charts)) return [];

  // Letter 612 pt − margins 36/36 = 540 pt usables.
  const FULL = 520;
  const SMALL = 300;

  type Chart = { key: string; label: string; width: number; align: "left" | "center" };
  // Orden de aparición:
  //   1. Tendencia (full) — timeline central del periodo
  //   2. Gastos por categoría (full) — detalle de gasto
  //   3. Margen por proyecto (full) — detalle por proyecto/cliente
  //   4. Salud financiera (small, centrado) — gauge, info ya en KPIs
  const order: Chart[] = [
    {
      key: "tendencia",
      label: "Tendencia · Ingresos vs gastos · margen acumulado",
      width: FULL,
      align: "center",
    },
    {
      key: "gastos-categoria",
      label: "Gastos por categoría · top del rango",
      width: FULL,
      align: "center",
    },
    {
      key: "margen-proyecto",
      label: "Utilidad bruta por proyecto · ingresos − gastos",
      width: FULL,
      align: "center",
    },
    {
      key: "salud-financiera",
      label: "Salud financiera · burn rate",
      width: SMALL,
      align: "center",
    },
  ];

  const content: import("pdfmake/interfaces").Content[] = [{ text: "Gráficas", style: "h2" }];

  for (const c of order) {
    const img = charts[c.key];
    if (!img) continue;
    content.push({
      stack: [
        {
          text: c.label,
          fontSize: 8,
          color: BRAND.textMuted,
          characterSpacing: 0.5,
          margin: [0, 0, 0, 4],
        },
        { image: img, width: c.width, alignment: c.align },
      ],
      margin: [0, 0, 0, 14],
    });
  }

  return content;
}

// Aliases para evitar warnings de imports no usados — pdfmake los toca al runtime.
void PAYMENT_METHOD_LABEL;
